import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { buildWhatsAppMessage } from '../lib/whatsappShare';

const API = process.env.REACT_APP_BACKEND_URL;

const WhatsAppIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12.05 0C5.49 0 .2 5.29.2 11.85c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.85 11.85 0 0 0 5.65 1.44h.01c6.55 0 11.85-5.3 11.85-11.85 0-3.17-1.24-6.15-3.39-8.43zM12.06 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.22-3.8 1 1.02-3.7-.24-.38a9.86 9.86 0 0 1-1.52-5.27c0-5.45 4.43-9.88 9.89-9.88 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 0 1 2.9 6.99c0 5.45-4.43 9.94-9.83 9.94zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.93 1.17-.17.2-.34.22-.63.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.53.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.19-.24-.58-.48-.5-.66-.51h-.56c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.5 1.69.64.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z"/>
  </svg>
);

// Module-level cache so all buttons share the same list without hitting the API repeatedly
let cachedContacts = null;
let cachedAt = 0;
let inflight = null;

async function loadFrequentContacts() {
  const now = Date.now();
  if (cachedContacts && now - cachedAt < 60_000) return cachedContacts;
  if (inflight) return inflight;
  inflight = axios
    .get(`${API}/api/family/frequent-contacts`, { withCredentials: true })
    .then((r) => {
      cachedContacts = r.data || [];
      cachedAt = Date.now();
      inflight = null;
      return cachedContacts;
    })
    .catch(() => {
      inflight = null;
      return [];
    });
  return inflight;
}

export function invalidateFrequentContactsCache() {
  cachedContacts = null;
  cachedAt = 0;
}

function normalizePhone(phone) {
  // WhatsApp requires digits only, no + or spaces
  return (phone || '').replace(/[^0-9]/g, '');
}

function openWhatsApp(text, phone) {
  const encoded = encodeURIComponent(text);
  const base = phone ? `https://wa.me/${normalizePhone(phone)}` : 'https://wa.me/';
  const url = `${base}?text=${encoded}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export default function ShareWhatsappButton({
  title,
  date,
  description,
  amount,
  currency,
  kind = 'reminder',
  withLabel = false,
  variant = 'ghost',
  testId,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    loadFrequentContacts().then(setContacts);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const text = buildWhatsAppMessage({ title, date, description, amount, currency, kind });

  const shareWithContact = async (member) => {
    setOpen(false);
    try {
      await axios.post(
        `${API}/api/family/members/${member.id}/share`,
        {},
        { withCredentials: true }
      );
      invalidateFrequentContactsCache();
    } catch (err) {
      // Non-blocking: still open WhatsApp even if the share counter update fails
      console.warn('[share] failed to record family share', err);
    }
    openWhatsApp(text, member.phone);
  };

  const shareWithOther = () => {
    setOpen(false);
    openWhatsApp(text, null);
  };

  const handleTrigger = (e) => {
    e.stopPropagation();
    // If no contacts saved yet, share directly (backwards-compatible one-click behavior)
    if (!contacts || contacts.length === 0) {
      openWhatsApp(text, null);
      return;
    }
    setOpen((v) => !v);
  };

  const base =
    variant === 'solid'
      ? 'bg-emerald-500 text-white hover:bg-[#1EBE5D] shadow-sm'
      : 'text-emerald-500 dark:text-emerald-400 hover:bg-emerald-500/10';

  const size = withLabel ? 'px-4 py-2 rounded-full text-sm font-semibold' : 'p-2 rounded-full';

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleTrigger}
        data-testid={testId || 'share-whatsapp-btn'}
        title="Compartir por WhatsApp"
        className={`inline-flex items-center gap-2 transition-all ${base} ${size}`}
      >
        <WhatsAppIcon className={withLabel ? 'w-4 h-4' : 'w-5 h-5'} />
        {withLabel && <span>Compartir por WhatsApp</span>}
      </button>

      {open && contacts && contacts.length > 0 && (
        <div
          ref={popoverRef}
          data-testid="share-contacts-popover"
          className="absolute right-0 top-full mt-2 z-50 w-64 bg-white rounded-2xl shadow-2xl border border-black/5 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-primary">Contactos frecuentes</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 text-slate-400 hover:text-slate-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {contacts.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`share-with-${c.id}`}
                onClick={() => shareWithContact(c)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-emerald-500/5 transition-all"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2A4A6F] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {c.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#1E3A5F] truncate">{c.name}</p>
                  <p className="text-xs text-slate-500 truncate">{c.phone}</p>
                </div>
                {c.share_count > 0 && (
                  <span className="text-[10px] font-semibold text-primary bg-amber-100 dark:bg-amber-500/15 px-2 py-0.5 rounded-full">
                    {c.share_count}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="share-with-other-btn"
            onClick={shareWithOther}
            className="w-full px-4 py-3 border-t border-black/5 text-sm font-semibold text-[#1E3A5F] hover:bg-slate-50 transition-all"
          >
            Otro contacto…
          </button>
        </div>
      )}
    </div>
  );
}
